// react
import { useMemo } from 'react';

// material ui
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import {
  gridColumnDefinitionsSelector,
  gridColumnVisibilityModelSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-premium';
// types
import type { GridColumnGroup } from '@mui/x-data-grid-premium';

/** Fields that should never appear in the columns panel. */
const PANEL_EXCLUDED_FIELDS = new Set([
  'favorites',
  'type',
  'linked_energy_groups',
  'main_address',
  'address',
  'ExtractedBuildingUse',
  'warnings',
]);

// MUI adds internal columns with __ prefix, e.g. tree-data grouping fields.
const isInternalField = (field: string) => field.startsWith('__');

interface Props {
  columnGroupingModel?: GridColumnGroup[];
}

interface ColumnGroup {
  id: string;
  label: string;
  fields: string[];
}

const CustomColumnsPanel = ({ columnGroupingModel = [] }: Props) => {
  const apiRef = useGridApiContext();

  const allColumns = useGridSelector(apiRef, gridColumnDefinitionsSelector);

  const visibilityModel = useGridSelector(
    apiRef,
    gridColumnVisibilityModelSelector,
  );

  const toggleableFields = useMemo(
    () =>
      new Set(
        allColumns
          .filter(
            (column) =>
              !PANEL_EXCLUDED_FIELDS.has(column.field) &&
              !isInternalField(column.field),
          )
          .map((column) => column.field),
      ),
    [allColumns],
  );

  /**
   * Only include columns that:
   * - belong to a column group
   * - belong to a group with a headerName
   * - are toggleable
   *
   * Ungrouped columns are intentionally ignored.
   */
  const groups = useMemo<ColumnGroup[]>(() => {
    return columnGroupingModel.flatMap((group) => {
      if (!group.headerName) {
        return [];
      }

      const fields = (group.children ?? [])
        .filter(
          (child): child is { field: string } =>
            'field' in child && toggleableFields.has(child.field),
        )
        .map((child) => child.field);

      if (fields.length === 0) {
        return [];
      }

      return [
        {
          id: group.groupId,
          label: group.headerName,
          fields,
        },
      ];
    });
  }, [columnGroupingModel, toggleableFields]);

  const isVisible = (field: string) => visibilityModel[field] !== false;

  const setVisible = (field: string, visible: boolean) => {
    apiRef.current.setColumnVisibility(field, visible);
  };

  const getColumnLabel = (field: string, groupLabel: string) => {
    const column = allColumns.find((column) => column.field === field);
    const headerName = column?.headerName ?? field;

    // Strip prefixes like "Water: Consumption" -> "Consumption"
    if (headerName.startsWith(`${groupLabel}: `)) {
      return headerName.slice(groupLabel.length + 2);
    }

    return headerName;
  };

  return (
    <Stack
      sx={{
        p: 1,
        minWidth: 220,
        maxHeight: 500,
        overflowY: 'auto',
      }}
    >
      {groups.map((group, index) => (
        <Box key={group.id}>
          {index > 0 && <Divider sx={{ my: 0.5 }} />}

          <Typography
            variant="body2"
            fontWeight="bold"
            sx={{
              px: 1,
              pt: index > 0 ? 1 : 0,
            }}
          >
            {group.label}
          </Typography>

          <Stack sx={{ pl: 2 }}>
            {group.fields.map((field) => (
              <FormControlLabel
                key={field}
                sx={{ ml: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={isVisible(field)}
                    onChange={(_, checked) => setVisible(field, checked)}
                  />
                }
                label={
                  <Typography variant="body2">
                    {getColumnLabel(field, group.label)}
                  </Typography>
                }
              />
            ))}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
};

export default CustomColumnsPanel;
